import {Injectable, OnDestroy, Renderer2, RendererFactory2} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {
  ColorConfig,
  ThemeableComponentConfig,
  ThemeableComponentName,
  ThemeConfig,
  ThemeMainColorType,
} from './theme-config';
import {createTheme, DEFAULT_THEME} from './default-theme';
import {Store} from '@ngrx/store';
import {palette, PaletteKeys, toHsl, toHslString} from '~utils/colors-fn';
import {loadPalette} from '~feature/feature-app/feature-app.selectors';
import {getAuth} from '~feature/feature-auth/feature-auth.selectors';
import {JwtHelperService} from '@auth0/angular-jwt';
import {getDetailsScopes} from '~core/security/auth.roles';

@Injectable({
  providedIn: 'root',
})
export class ThemingService implements OnDestroy {
  private static readonly THEME_VARIABLE_PREFIX = '--theme';
  currentTheme: BehaviorSubject<ThemeConfig> = new BehaviorSubject<ThemeConfig>(DEFAULT_THEME);
  private readonly renderer: Renderer2;

  constructor(
    private rendererFactory: RendererFactory2,
    private jtw: JwtHelperService,
    private store: Store
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  ngOnDestroy(): void {
    this.renderer?.destroy();
  }

  initialize(): void {
    this.store.select(getAuth).subscribe(r => {
      if (r.tokenRules) {
        const {theme} = getDetailsScopes(this.jtw, r.tokenRules);
        if (theme) {
          this.changeColor(theme);
        }
      }
    });
    this.store.select(loadPalette).subscribe(color => this.changeColor(color));
    this.subscribeToThemeChanges();
  }

  private subscribeToThemeChanges(): void {
    this.currentTheme.subscribe((themeConfig: ThemeConfig) => {
      this.setupMainPalettes(themeConfig);
      this.setupComponentVariables(themeConfig);
    });
  }

  private changeColor(color: string): void {
    let config: ThemeConfig;
    if (!color || color.toLowerCase() === DEFAULT_THEME.mainColors.primary.toLowerCase()) {
        config = DEFAULT_THEME;
    } else {
        config = createTheme(color, accentColorGen(color));
    }
    if (this.currentTheme.value.themeId !== config.themeId) {
        this.currentTheme.next(config);
    }
  }

  private setupMainPalettes(themeConfig: ThemeConfig): void {
    Object.keys(themeConfig.mainColors).forEach((key: string) => {
      const selectedColorValue: string = themeConfig.mainColors[key as ThemeMainColorType];
      const variableName: string = this.prependVariableName(this.convertCamelCaseToKebabCase(key));
      const colorPalette: Array<ColorConfig> = this.generateColorPalette(selectedColorValue);

      colorPalette.forEach((colorConfig: ColorConfig) => {
        const {colorVariant, colorHexValue, shouldHaveDarkContrast} = colorConfig;

        const colorVariableName = `${variableName}-${colorVariant}`;
        this.setColorVariable(colorVariableName, colorHexValue);

        const contrastedColorVariableName = `${variableName}-contrast-${colorVariant}`;
        const contrastedColorValue = constrastedColor(shouldHaveDarkContrast);
        this.setColorVariable(contrastedColorVariableName, contrastedColorValue);
      });
    });
  }

  private generateColorPalette(hexColor: string): Array<ColorConfig> {
    return PaletteKeys.map(key => this.mapColor(...palette(hexColor, key)));
  }

  private mapColor(colorHexValue: string, shouldHaveDarkContrast: boolean, colorVariant: string): ColorConfig {
    return {colorVariant, colorHexValue, shouldHaveDarkContrast};
  }

  private setupComponentVariables(theme: ThemeConfig): void {
    const {components} = theme;
    if (!components) {
      return;
    }
    Object.keys(components).forEach((componentName: string) => {
      const componentConfig: ThemeableComponentConfig = components[componentName as unknown as ThemeableComponentName];
      Object.keys(componentConfig.colors).forEach((property: string) => {
        const key = this.convertCamelCaseToKebabCase(`${componentName}-${property}`);
        const cssVariable = this.prependVariableName(key);
        const colorHex = (componentConfig.colors as { [k: string]: string })[property];
        const colorHSL = toHsl(colorHex);

        this.setHSLVariables(cssVariable, colorHSL);
        this.setColorVariable(cssVariable, toHslString(colorHex));
      });
    });
  }

  private setHSLVariables(variableName: string, [h, s, l]: [number, number, number]): void {
    this.setColorVariable(`${variableName}-h`, `${Math.round(h)}`);
    this.setColorVariable(`${variableName}-s`, `${Math.round(s * 100)}%`);
    this.setColorVariable(`${variableName}-l`, `${Math.round(l * 100)}%`);
  }

  private prependVariableName(key: string): string {
    return `${ThemingService.THEME_VARIABLE_PREFIX}-${key}`;
  }

  private convertCamelCaseToKebabCase(key: string): string {
    return key.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
  }

  private setColorVariable(variable: string, color: string): void {
    document.documentElement.style.setProperty(variable, color);
  }
}

export const accentColorGen = (color: string) => palette(color, '800')[0];

export const lightColorGen = (color: string) => palette(color, '50')[0];

export const constrastedColor = (isLight: boolean) => isLight ? 'rgba(0, 0, 0, 0.87)' : '#fff';
